// components/auth/RegistrationRejectedPage.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { 
  XCircle, 
  Mail, 
  MessageCircle,
  FileText,
  Home,
  LogOut,
  AlertCircle,
  ArrowRight
} from 'lucide-react';
import tunaLogo from '../../assets/tunasignalai_logo.png';
import { SiteStyles } from '../site/SiteChrome';

const RegistrationRejectedPage = () => {
  useTheme();
  const darkMode = true; // public and account pages always use the site's dark terminal theme
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleEmailSupport = () => {
    window.location.href = 'mailto:support@tunasignal.ai?subject=Registration%20Rejected%20-%20Appeal%20Request';
  };

  const handleNewApplication = () => {
    // Clear any stored registration data
    localStorage.removeItem('user_id');
    sessionStorage.clear();
    navigate('/register');
  };

  return (
    <div className="dark ts-page">
    <SiteStyles />
    <div className={`min-h-screen ${
      darkMode ? 'bg-[#07131F]' : 'bg-slate-50 dark:bg-[#13283C]'
    }`}>
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className={`w-full max-w-2xl ${
          darkMode ? 'bg-[#0D1E2E]' : 'bg-white'
        } rounded-md overflow-hidden`}>
          
          {/* Rejection Header */}
          <div className="relative bg-[#0D1E2E] border-b border-[#1C3348] p-8 text-white">
            
            <div className="relative">
              <XCircle className="w-7 h-7 mb-3 text-[#F0414F]" aria-hidden="true" />
              <h1 className="text-2xl font-semibold mb-1">Registration Not Approved</h1>
              <p className="text-slate-400">
                We were unable to approve your registration at this time
              </p>
            </div>
          </div>

          {/* Main Content */}
          <div className="p-8">
            {/* Reason Section */}
            <div className={`rounded-lg p-6 mb-6 border ${
              darkMode ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h2 className={`text-lg font-semibold mb-2 ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    Why was my registration not approved?
                  </h2>
                  <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Common reasons for registration rejection include:
                  </p>
                  <ul className={`mt-3 space-y-2 text-sm ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    <li>• Incomplete or inaccurate information provided</li>
                    <li>• Unable to verify professional credentials</li>
                    <li>• Registration from restricted regions</li>
                    <li>• Duplicate account detected</li>
                    <li>• Not meeting minimum requirements for platform access</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Appeal Process */}
            <div className={`rounded-lg border ${
              darkMode ? 'border-[#1C3348]' : 'border-gray-200'
            } p-6 mb-6`}>
              <h3 className={`font-semibold mb-4 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                What Can You Do Next?
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <Mail className="w-5 h-5 text-blue-500 mr-3 mt-0.5" />
                  <div>
                    <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Appeal the Decision
                    </p>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      If you believe this was a mistake, contact our support team with additional information
                    </p>
                    <button
                      onClick={handleEmailSupport}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium mt-2 flex items-center"
                    >
                      Contact Support
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </button>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <FileText className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                  <div>
                    <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Submit a New Application
                    </p>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      You may reapply with corrected or additional information
                    </p>
                    <button
                      onClick={handleNewApplication}
                      className="text-green-600 hover:text-green-700 text-sm font-medium mt-2 flex items-center"
                    >
                      Start New Application
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </button>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <MessageCircle className="w-5 h-5 text-slate-400 mr-3 mt-0.5" />
                  <div>
                    <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Learn More
                    </p>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Review our eligibility requirements and application guidelines
                    </p>
                    <button
                      onClick={() => navigate('/learn')}
                      className="text-slate-400 hover:text-purple-700 text-sm font-medium mt-2 flex items-center"
                    >
                      View Requirements
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Notice */}
            <div className={`rounded-lg p-4 mb-6 ${
              darkMode ? 'bg-yellow-900/20' : 'bg-yellow-50'
            }`}>
              <p className={`text-sm ${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                <strong>Note:</strong> If you wish to appeal or reapply, please ensure you have all necessary documentation ready. 
                Our team reviews appeals within 2-3 business days.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleEmailSupport}
                className={`w-full px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center ${
                  darkMode 
                    ? 'bg-[#5AA9F0] hover:bg-[#8CC4F5] text-[#07131F]' 
                    : 'bg-[#5AA9F0] hover:bg-[#8CC4F5] text-[#07131F]'
                }`}
              >
                <Mail className="w-5 h-5 mr-2" />
                Contact Support Team
              </button>
              
              <button
                onClick={() => navigate('/')}
                className={`w-full px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center border ${
                  darkMode 
                    ? 'border-[#28445F] hover:bg-[#13283C] text-gray-300' 
                    : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                }`}
              >
                <Home className="w-5 h-5 mr-2" />
                Return to Home
              </button>
              
              <button
                onClick={handleLogout}
                className={`w-full px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center ${
                  darkMode 
                    ? 'text-red-400 hover:bg-red-900/20' 
                    : 'text-red-600 hover:bg-red-50'
                }`}
              >
                <LogOut className="w-5 h-5 mr-2" />
                Sign Out
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className={`px-8 py-4 border-t ${
            darkMode ? 'bg-[#07131F] border-[#1C3348]' : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center justify-between">
              <img src={tunaLogo} alt="TunaSignal AI" className="h-6" />
              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                Reference ID: {Date.now().toString(36).toUpperCase()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default RegistrationRejectedPage;